import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  getOverviewKpis,
  getActivityTrend,
  getAgentActivityRank,
  getHotMaterials,
  type TimeFilter,
  type OverviewKpis,
  type ActivityTrendPoint,
  type AgentActivityRankItem,
  type HotMaterialItem,
  type HotMaterialSortBy,
} from "@/api/dashboard";
import { getTree, type CategoryNode } from "@/api/materialCategory";

type ChartRange = "7days" | "15days" | "30days" | "1year" | "yearly";

function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  return d;
}

interface LineChartProps {
  data: ActivityTrendPoint[];
  chartRange: ChartRange;
  isHourly?: boolean;
  isYearly?: boolean;
}

function LineChart({ data, chartRange, isHourly, isYearly }: LineChartProps) {
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    views: number;
    downloads: number;
    date: string;
    flip: boolean;
  } | null>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const W = 800;
  const H = 200;
  const padL = 38;
  const padR = 8;
  const padT = 12;
  const padB = 28;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const gridLines = 4;

  // 1年模式按周聚合，年度数据无需聚合 —— hooks 必须在条件 return 之前
  const displayData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (isYearly || isHourly || chartRange !== "1year" || data.length <= 60) return data;
    const weeks: ActivityTrendPoint[] = [];
    for (let i = 0; i < data.length; i += 7) {
      const slice = data.slice(i, i + 7);
      weeks.push({
        timeSlot: slice[0].timeSlot,
        views: Math.round(slice.reduce((s, d) => s + d.views, 0) / slice.length),
        downloads: Math.round(slice.reduce((s, d) => s + d.downloads, 0) / slice.length),
      });
    }
    return weeks;
  }, [data, chartRange, isHourly, isYearly]);

  // 空数据保护（所有 hooks 之后）
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-gray-800">活动趋势</h3>
            <p className="text-xs text-gray-400 mt-0.5">浏览量与下载量变化</p>
          </div>
        </div>
        <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
          暂无活动数据
        </div>
      </div>
    );
  }

  const values = displayData.map((d) => d.views + d.downloads);
  const maxVal = Math.max(...values, 1);

  const pts = displayData.map((d, i) => ({
    x: padL + (i / Math.max(displayData.length - 1, 1)) * chartW,
    yViews: padT + chartH - (d.views / maxVal) * chartH,
    yDl: padT + chartH - (d.downloads / maxVal) * chartH,
    ...d,
  }));

  const viewsCurve = smoothPath(pts.map((p) => ({ x: p.x, y: p.yViews })));
  const dlCurve = smoothPath(pts.map((p) => ({ x: p.x, y: p.yDl })));
  const fillViews =
    viewsCurve +
    ` L ${pts[pts.length - 1]?.x ?? padL} ${padT + chartH} L ${padL} ${padT + chartH} Z`;

  // 标签步长：今日=每4小时, 年度=全部显示, 其他按点数自适应
  const labelStep = isHourly
    ? 4
    : isYearly
      ? 1
      : Math.max(1, Math.floor(displayData.length / (chartRange === "1year" ? 12 : chartRange === "30days" ? 8 : 7)));

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 p-5 mb-4"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-800">活动趋势</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {isHourly ? "24小时浏览与下载" : isYearly ? "年度浏览与下载" : "浏览量与下载量变化"}
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-0.5 rounded-full bg-blue-600"></span>浏览
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-0.5 rounded-full bg-amber-600"></span>下载
          </span>
        </div>
      </div>

      {/* SVG Chart */}
      <div
        className="relative select-none"
        onMouseLeave={() => {
          setTooltip(null);
          setHoveredIdx(null);
        }}
      >
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: "200px", display: "block" }}
        >
          <defs>
            <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563EB" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Grid */}
          {Array.from({ length: gridLines + 1 }).map((_, i) => {
            const y = padT + (i / gridLines) * chartH;
            const val = Math.round(maxVal - (i / gridLines) * maxVal);
            return (
              <g key={i}>
                <line
                  x1={padL}
                  y1={y}
                  x2={W - padR}
                  y2={y}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                  strokeDasharray={i === gridLines ? "" : "4 3"}
                />
                <text
                  x={padL - 6}
                  y={y + 3.5}
                  textAnchor="end"
                  fontSize="8"
                  fill="#d1d5db"
                >
                  {val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val}
                </text>
              </g>
            );
          })}
          {/* X labels */}
          {pts
            .filter((_, i) => i % labelStep === 0 || i === pts.length - 1)
            .map((p) => {
              const label = isHourly
                ? p.timeSlot.slice(11, 16)
                : isYearly
                  ? p.timeSlot
                  : chartRange === "1year"
                    ? p.timeSlot.slice(0, 7)
                    : p.timeSlot.slice(5);
              return (
                <text
                  key={p.timeSlot}
                  x={p.x}
                  y={H - 6}
                  textAnchor="middle"
                  fontSize="7.5"
                  fill="#d1d5db"
                >
                  {label}
                </text>
              );
            })}
          {/* Fill area */}
          {pts.length > 1 && <path d={fillViews} fill="url(#viewsFill)" />}
          {/* Lines */}
          <path
            d={dlCurve}
            fill="none"
            stroke="#D97706"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={viewsCurve}
            fill="none"
            stroke="#2563EB"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Hover areas */}
          {pts.map((p, i) => (
            <g key={p.timeSlot}>
              {hoveredIdx === i && (
                <line
                  x1={p.x}
                  y1={padT}
                  x2={p.x}
                  y2={padT + chartH}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="3 2"
                />
              )}
              <circle
                cx={p.x}
                cy={p.yViews}
                r={hoveredIdx === i ? 5 : 2.5}
                fill="#374151"
                stroke="white"
                strokeWidth="1.5"
                style={{ transition: "r 0.12s ease" }}
              />
              <circle
                cx={p.x}
                cy={p.yDl}
                r={hoveredIdx === i ? 5 : 2.5}
                fill="#9ca3af"
                stroke="white"
                strokeWidth="1.5"
                style={{ transition: "r 0.12s ease" }}
              />
              <rect
                x={p.x - chartW / Math.max(pts.length - 1, 1) / 2}
                y={padT}
                width={chartW / Math.max(pts.length - 1, 1)}
                height={chartH}
                fill="transparent"
                style={{ cursor: "crosshair" }}
                onMouseEnter={(e) => {
                  setHoveredIdx(i);
                  const svgEl = e.currentTarget.closest("svg") as SVGSVGElement;
                  const rect = svgEl.getBoundingClientRect();
                  setTooltip({
                    x: (p.x / W) * rect.width + rect.left,
                    y: (Math.min(p.yViews, p.yDl) / H) * rect.height + rect.top,
                    views: p.views,
                    downloads: p.downloads,
                    date: p.timeSlot,
                    flip: p.x > padL + chartW * 0.8,
                  });
                }}
                onMouseLeave={() => {
                  setHoveredIdx(null);
                  setTooltip(null);
                }}
              />
            </g>
          ))}
        </svg>
        {tooltip && (
          <div
            className="fixed z-50 pointer-events-none rounded-xl text-xs font-semibold text-gray-700 bg-white border border-gray-100 px-3 py-2"
            style={{
              left: tooltip.flip ? tooltip.x - 14 : tooltip.x + 14,
              top: tooltip.y - 48,
              transform: tooltip.flip ? 'translateX(-100%)' : undefined,
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
            }}
          >
            <p className="text-gray-400 font-normal mb-1">{tooltip.date}</p>
            <p className="text-gray-700">浏览 {tooltip.views.toLocaleString()}</p>
            <p className="text-gray-400">下载 {tooltip.downloads.toLocaleString()}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** 格式化时间戳为可读日期 */
function formatLastActive(ts: number): string {
  if (!ts || ts === 0) return "—";
  const d = new Date(ts);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

export default function AdminDashboard() {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("30days");
  const [loading, setLoading] = useState(true);
  const [materialLoading, setMaterialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [materialError, setMaterialError] = useState<string | null>(null);

  // 各模块数据
  const [kpis, setKpis] = useState<OverviewKpis | null>(null);
  const [trendData, setTrendData] = useState<ActivityTrendPoint[]>([]);
  const [agentRank, setAgentRank] = useState<AgentActivityRankItem[]>([]);
  const [hotMaterials, setHotMaterials] = useState<HotMaterialItem[]>([]);
  const [materialRankSort, setMaterialRankSort] = useState<HotMaterialSortBy>("views");
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);
  const materialRequestSeq = useRef(0);

  // 二级分类 ID → 显示名称 的映射表
  const categoryLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const primary of categoryTree) {
      for (const sub of primary.subCategories) {
        map[sub.id] = sub.label;
      }
    }
    return map;
  }, [categoryTree]);

  const fetchOverviewData = useCallback(async (filter: TimeFilter) => {
    setLoading(true);
    setError(null);
    try {
      const [kpiResult, trendResult, agentResult] = await Promise.all([
        getOverviewKpis(filter),
        getActivityTrend(filter),
        getAgentActivityRank(filter, 20),
      ]);
      setKpis(kpiResult);
      setTrendData(trendResult);
      setAgentRank(agentResult);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "数据加载失败";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHotMaterials = useCallback(async (filter: TimeFilter, sortBy: HotMaterialSortBy) => {
    const requestSeq = ++materialRequestSeq.current;
    setMaterialLoading(true);
    setMaterialError(null);
    try {
      const materialResult = await getHotMaterials(filter, 20, sortBy);
      if (requestSeq !== materialRequestSeq.current) return;
      setHotMaterials(materialResult);
    } catch (err) {
      if (requestSeq !== materialRequestSeq.current) return;
      const msg = err instanceof Error ? err.message : "热门资料加载失败";
      setHotMaterials([]);
      setMaterialError(msg);
    } finally {
      if (requestSeq === materialRequestSeq.current) {
        setMaterialLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchOverviewData(timeFilter);
  }, [timeFilter, fetchOverviewData]);

  useEffect(() => {
    fetchHotMaterials(timeFilter, materialRankSort);
  }, [timeFilter, materialRankSort, fetchHotMaterials]);

  useEffect(() => {
    getTree().then(setCategoryTree).catch(() => {});
  }, []);

  const chartRange: ChartRange =
    timeFilter === "today"
      ? "7days"
      : timeFilter === "7days"
        ? "7days"
        : timeFilter === "30days"
          ? "30days"
          : timeFilter === "all"
            ? "yearly"
            : "1year";

  // 今日数据为24小时粒度，全部数据为年度粒度
  const isHourly = timeFilter === "today";
  const isYearly = timeFilter === "all";

  const periodLabel =
    timeFilter === "today"
      ? "今日"
      : timeFilter === "7days"
        ? "7天"
        : timeFilter === "30days"
          ? "30天"
          : timeFilter === "1year"
            ? "1年"
            : "累计";

  const filterOptions: { value: TimeFilter; label: string }[] = [
    { value: "today", label: "今日" },
    { value: "7days", label: "7天" },
    { value: "30days", label: "30天" },
    { value: "1year", label: "1年" },
    { value: "all", label: "全部" },
  ];

  const kpiCards = [
    {
      label: "代理商总数",
      value: kpis?.totalAgents ?? 0,
      icon: "ri-team-line",
      sub: `活跃 ${kpis?.activeAgents ?? 0} 位`,
      badge: `共 ${kpis?.totalAgents ?? 0} 位`,
      from: "#0069BD",
      to: "#0085EB",
      lightBg: "#E6F2FA",
      iconColor: "#0069BD",
    },
    {
      label: "资料总数",
      value: kpis?.totalMaterials ?? 0,
      icon: "ri-folder-5-line",
      sub: "全部资料",
      badge: "全部",
      from: "#0069BD",
      to: "#0085EB",
      lightBg: "#E6F2FA",
      iconColor: "#0069BD",
    },
    {
      label: "期间浏览量",
      value: (kpis?.periodViews ?? 0).toLocaleString(),
      icon: "ri-eye-line",
      sub: periodLabel,
      badge: "浏览",
      from: "#0069BD",
      to: "#0085EB",
      lightBg: "#E6F2FA",
      iconColor: "#0069BD",
    },
    {
      label: "期间下载量",
      value: (kpis?.periodDownloads ?? 0).toLocaleString(),
      icon: "ri-download-cloud-line",
      sub: periodLabel,
      badge: "下载",
      from: "#0069BD",
      to: "#0085EB",
      lightBg: "#E6F2FA",
      iconColor: "#0069BD",
    },
  ];

  return (
    <AdminLayout title="数据概览" subtitle="代理商活动与资料使用统计">
      {/* Time Filter */}
      <div className="flex items-center justify-between mb-5">
        <div className="inline-flex bg-gray-200 rounded-lg p-1">
          {filterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setTimeFilter(opt.value)}
              style={
                timeFilter === opt.value
                  ? { boxShadow: "0 1px 4px rgba(0,0,0,0.18)" }
                  : {}
              }
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap cursor-pointer ${
                timeFilter === opt.value
                  ? "bg-white text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {/* 加载指示器 */}
        {loading && (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <i className="ri-loader-4-line animate-spin"></i> 加载中...
          </span>
        )}
        {error && (
          <span className="text-xs text-red-400 flex items-center gap-1">
            <i className="ri-error-warning-line"></i> {error}
          </span>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        {kpiCards.map((k) => (
          <div
            key={k.label}
            className="relative rounded-2xl overflow-hidden bg-white border border-gray-100"
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
          >
            <div
              className="h-1.5 w-full"
              style={{ background: `linear-gradient(90deg, ${k.from}, ${k.to})` }}
            />
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: k.lightBg }}
                >
                  <i
                    className={`${k.icon} text-base`}
                    style={{ color: k.iconColor }}
                  ></i>
                </div>
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ backgroundColor: k.lightBg, color: k.iconColor }}
                >
                  {k.badge}
                </span>
              </div>
              <p className="text-3xl font-extrabold text-gray-900 leading-none tracking-tight mb-1">
                {loading ? (
                  <span className="inline-block w-16 h-8 bg-gray-100 rounded animate-pulse" />
                ) : (
                  k.value
                )}
              </p>
              <p className="text-sm font-medium text-gray-500 leading-snug">
                {k.label}
              </p>
              <p className="text-xs mt-1 font-medium" style={{ color: k.iconColor }}>
                {k.sub}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Full-width Chart */}
      <LineChart
        data={trendData}
        chartRange={chartRange}
        isHourly={isHourly}
        isYearly={isYearly}
      />

      {/* Bottom Row: Agent table + Material ranking side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Agent Activity Table */}
        <div
          className="bg-white rounded-xl border border-gray-100 overflow-hidden"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
        >
          <div className="px-5 py-3.5 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800">代理商活跃度排行榜 Top20</h3>
          </div>
          <div className="max-h-[640px] overflow-y-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-50 bg-gray-50/95 backdrop-blur-sm">
                {["代理商", "地区", "浏览", "下载", "最近活跃"].map((h) => (
                  <th
                    key={h}
                    className="sticky top-0 z-10 text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap bg-gray-50/95 backdrop-blur-sm"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className="px-4 py-2.5">
                        <div className="h-4 bg-gray-100 rounded animate-pulse w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : agentRank.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    暂无活动数据
                  </td>
                </tr>
              ) : (
                agentRank.map((agent) => (
                  <tr key={agent.userId} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0"
                          style={{
                            background: "#0069BD",
                            fontSize: "9px",
                            fontWeight: 700,
                          }}
                        >
                          {agent.name
                            .split(" ")
                            .map((n: string) => n[0])
                            .join("")
                            .slice(0, 2)}
                        </div>
                        <span className="text-xs font-semibold text-gray-800 whitespace-nowrap truncate max-w-[90px]">
                          {agent.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {agent.region}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-semibold text-gray-700 whitespace-nowrap">
                      {agent.views.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">
                      {agent.downloads.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                      {formatLastActive(agent.lastActive)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Top Materials */}
        <div
          className="bg-white rounded-xl border border-gray-100 overflow-hidden"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
        >
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="text-sm font-bold text-gray-800 whitespace-nowrap">资料热门度排行榜 Top20</h3>
              {materialError && (
                <span className="text-xs text-red-400 truncate">
                  {materialError}
                </span>
              )}
            </div>
            <div className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5 shrink-0">
              {[
                { key: "views" as const, label: "按浏览量" },
                { key: "downloadRate" as const, label: "按下载率" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setMaterialRankSort(item.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    materialRankSort === item.key
                      ? "bg-white text-gray-800 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[640px] overflow-y-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-50 bg-gray-50/95 backdrop-blur-sm">
                {["排名", "资料名称", "分类", "浏览", "下载率"].map((h) => (
                  <th
                    key={h}
                    className="sticky top-0 z-10 text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap bg-gray-50/95 backdrop-blur-sm"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {materialLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className="px-4 py-2.5">
                        <div className="h-4 bg-gray-100 rounded animate-pulse w-16" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : hotMaterials.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    暂无数据
                  </td>
                </tr>
              ) : (
                hotMaterials.map((m, i) => (
                  <tr key={m.materialId} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5">
                      <span
                        className={`text-sm font-bold ${i < 3 ? "text-gray-600" : "text-gray-300"}`}
                      >
                        #{i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="text-xs font-semibold text-gray-800 max-w-[140px] truncate">
                        {m.title}
                      </p>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="px-1.5 py-0.5 text-xs rounded-full border font-medium whitespace-nowrap bg-gray-100 text-gray-500 border-gray-200">
                        {(() => {
                          const label = categoryLabelMap[m.category] || m.category;
                          return label === "E-learning" ? "培训" : label;
                        })()}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs font-semibold text-gray-700 whitespace-nowrap">
                      {m.views.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {m.views > 0 ? `${Math.min(Math.round((m.downloads / m.views) * 100), 100)}%` : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
